import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import { KibButtonNew } from '@chewy/kib-controls-react'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import { makeStyles } from '@mui/styles'
import classNames from 'classnames'
import * as R from 'ramda'
import type { ClassesType } from '@pbt/pbt-ui-components'

import {
  type FilterFacetItem,
  FilterSection,
} from '~/components/common/filters/MultiSectionFiltersFacetsWidget'
import FeatureToggle from '~/constants/featureToggle'
import type { TimelineEntryType } from '~/constants/timelineConstants'
import { updateTimelineFilters } from '~/store/actions/timeline'
import { getFeatureToggle } from '~/store/reducers/constants'
import { getTimelineFilters } from '~/store/reducers/timeline'
import type { TimelineConfigItem } from '~/types'

import TimelineFilter from './TimelineFilter'
import useTimelineItems from './timelineItems'

import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'
import styles from './TimelineFilters.module.scss'

const useStyles = makeStyles(
  (theme) => ({
    root: {},
    filterRow: {
      width: 'fit-content',
    },
    filterContainer: {
      marginBottom: theme.spacing(2),
      width: 'fit-content',
    },
    toggleButton: {
      padding: '4px 13px',
      textTransform: 'none',
      color: theme.colors.lowAccentText,
    },
  }),
  { name: 'TimelineFilters' },
)

export interface TimelineFiltersProps {
  classes?: ClassesType<typeof useStyles>
  collapseConfig?: {
    isExpandedByDefault: boolean
    primaryFilters: TimelineEntryType[]
  }
  filtersSelectedByDefault?: TimelineEntryType[]
  isHorizontalLayout?: boolean
}

const TimelineFilters = ({
  classes: classesProp,
  collapseConfig,
  filtersSelectedByDefault,
  isHorizontalLayout = false,
}: TimelineFiltersProps) => {
  const dispatch = useDispatch()
  const classes = useStyles({ classes: classesProp })
  const { t } = useTranslation('Common')
  const [searchParams, setSearchParams] = useSearchParams()

  const filters = useSelector(getTimelineFilters)
  const isRxTrackerPhase1Enabled = useSelector(
    getFeatureToggle(FeatureToggle.RX_TRACKER_PHASE_1),
  )

  const useChirpChipFilters = isHorizontalLayout && isRxTrackerPhase1Enabled
  const useRxTrackerPhase1SidebarLayout =
    !isHorizontalLayout && isRxTrackerPhase1Enabled
  const useChirpStyleFilters =
    useChirpChipFilters || useRxTrackerPhase1SidebarLayout

  const urlFilters = searchParams.get('timelineFilters')
  const filtersFromStore = filters.join(',')
  const [isExpanded, setIsExpanded] = useState(
    collapseConfig?.isExpandedByDefault !== false,
  )
  const timelineItems = useTimelineItems()
  const filteredTimelineItems = Object.values(timelineItems).filter(
    (item) => !item.skipInFilters && item.permission,
  )

  const sortedTimelineItems: TimelineConfigItem[] = R.sortBy(
    R.pipe(R.prop('label'), R.toLower),
    filteredTimelineItems,
  )

  const primaryTimelineItems: TimelineConfigItem[] =
    collapseConfig?.primaryFilters
      ? filteredTimelineItems
          .filter(({ type }) => collapseConfig.primaryFilters.includes(type))
          .sort(
            (a, b) =>
              collapseConfig.primaryFilters.indexOf(a.type) -
              collapseConfig.primaryFilters.indexOf(b.type),
          )
      : []

  const isCollapsible =
    collapseConfig &&
    primaryTimelineItems.length > 0 &&
    primaryTimelineItems.length < sortedTimelineItems.length

  const timelineItemsToRender =
    isExpanded || !isCollapsible ? sortedTimelineItems : primaryTimelineItems

  const toggleIsExpanded = () =>
    setIsExpanded((prevIsExpanded) => !prevIsExpanded)

  const onSelect = (type: TimelineEntryType, selected: boolean) => {
    const prevFilters = filters.filter((filter) => filter !== type)
    const newFilters = selected ? [] : [type]
    const allFilters = [...prevFilters, ...newFilters]

    if (allFilters.length > 0) {
      searchParams.set('timelineFilters', allFilters.join(','))
    } else {
      searchParams.delete('timelineFilters')
    }
    setSearchParams(searchParams)

    dispatch(updateTimelineFilters(allFilters))
  }

  const onClear = () => {
    searchParams.delete('timelineFilters')
    setSearchParams(searchParams)

    dispatch(updateTimelineFilters([]))
  }

  useEffect(() => {
    if (filtersSelectedByDefault && filtersSelectedByDefault.length) {
      const prevFilters = filters.filter(
        (filter) => !filtersSelectedByDefault.includes(filter),
      )
      const allFilters = [...prevFilters, ...filtersSelectedByDefault]

      searchParams.set('timelineFilters', allFilters.join(','))
      setSearchParams(searchParams)
      dispatch(updateTimelineFilters(allFilters))
    }
  }, [filtersSelectedByDefault])

  // Sync params from URL with store, as they would be the source of truth and store needs to be in sync with it
  const storeAndUrlFiltersAreEqual = filtersFromStore === urlFilters
  useEffect(() => {
    if (!storeAndUrlFiltersAreEqual) {
      dispatch(
        updateTimelineFilters(
          urlFilters ? (urlFilters.split(',') as TimelineEntryType[]) : [],
        ),
      )
    }
  }, [storeAndUrlFiltersAreEqual])

  const itemMarginRight = isHorizontalLayout ? 1 : 0

  const filterItems: FilterFacetItem<TimelineEntryType>[] = useMemo(
    () =>
      timelineItemsToRender.map(({ label, type }) => ({
        id: type,
        label,
        value: type,
        enabled: filters.includes(type),
      })),
    [timelineItemsToRender, filters],
  )

  const handleFilterToggle = (
    _sectionKey: string,
    itemId: string | number,
    enabled: boolean,
  ) => {
    onSelect(itemId as TimelineEntryType, enabled)
  }

  const handleDeselectSection = () => {
    onClear()
  }

  if (useChirpStyleFilters) {
    const hasActiveFilters = filters.length > 0
    const chirpContent = (
      <div
        aria-label={t('Common:FILTER_RESULTS') as string}
        className={classNames(
          styles.chirpFiltersContainer,
          useRxTrackerPhase1SidebarLayout &&
            styles.chirpFiltersContainerVertical,
        )}
      >
        <div className={styles.chirpFiltersHeader}>
          <h3 className={styles.chirpFiltersTitle}>
            {t('Common:FILTER_RESULTS')}
          </h3>
          <div className={styles.chirpFiltersHeaderButtons}>
            {isCollapsible && (
              <KibButtonNew
                className={styles.deselectButton}
                emphasis="tertiary"
                size="small"
                theme="action"
                onClick={toggleIsExpanded}
              >
                {isExpanded
                  ? t('Common:LESS_FILTERS')
                  : t('Common:ALL_FILTERS')}
              </KibButtonNew>
            )}
            <KibButtonNew
              className={styles.deselectButton}
              disabled={!hasActiveFilters}
              emphasis="tertiary"
              size="small"
              theme="action"
              onClick={onClear}
            >
              {t('Common:DESELECT_ALL')}
            </KibButtonNew>
          </div>
        </div>
        <FilterSection
          hideHeader
          classes={
            useRxTrackerPhase1SidebarLayout
              ? { chipContainer: styles.chirpFiltersChipContainerVertical }
              : undefined
          }
          items={filterItems}
          sectionKey="timeline"
          sectionType="filter"
          toggleHandler={handleFilterToggle}
          onDeselectSection={handleDeselectSection}
        />
      </div>
    )

    if (useRxTrackerPhase1SidebarLayout) {
      return (
        <div
          aria-label={t('Common:FILTER_RESULTS') as string}
          className={styles.chirpSidebarWrapper}
        >
          {chirpContent}
        </div>
      )
    }

    return chirpContent
  }

  return (
    <Grid
      container
      item
      aria-label={t('Common:FILTER_RESULTS') as string}
      className={classes.filterRow}
      justifyContent="center"
      lg={isHorizontalLayout ? undefined : 4}
      style={{ flexBasis: isHorizontalLayout ? 1 : undefined }}
    >
      <Grid
        container
        item
        aria-label={t('Common:FILTER_RESULTS') as string}
        className={classes.filterRow}
        direction={isHorizontalLayout ? 'row' : 'column'}
        pl={isHorizontalLayout ? 0 : 1}
      >
        <Grid item className={classes.filterContainer} mr={itemMarginRight}>
          <TimelineFilter
            main
            label={t('Common:DESELECT_ALL')}
            selected={Boolean(filters.length)}
            onSelect={() => onClear()}
          />
        </Grid>
        {timelineItemsToRender.map(({ icon, label, important, type }) => {
          const selected = filters.indexOf(type) > -1

          return (
            <Grid
              item
              className={classes.filterContainer}
              key={label}
              mr={itemMarginRight}
            >
              <TimelineFilter
                icon={icon}
                important={important}
                label={label}
                selected={selected}
                onSelect={() => onSelect(type, selected)}
              />
            </Grid>
          )
        })}
        {isCollapsible && (
          <Grid
            item
            className={classNames(
              classes.filterContainer,
              classes.toggleButton,
            )}
            component={Button}
            endIcon={isExpanded ? <ExpandLess /> : <ExpandMore />}
            mr={itemMarginRight}
            variant="text"
            onClick={toggleIsExpanded}
          >
            {isExpanded ? t('Common:LESS_FILTERS') : t('Common:ALL_FILTERS')}
          </Grid>
        )}
      </Grid>
    </Grid>
  )
}

export default TimelineFilters
